/* eslint-disable new-cap */
import { getDocumentUrl, getLastSignatureUrl } from '../documents/utils';

import { CA_SERVERS } from '../../lib/constants';
import { BILLING_OVERLIMIT_ERROR_CODE } from '../../lib/errors';
import { getDocumentsByPartially } from '../documents/api';
import { ACSK } from '../enum';
import io from '../io';
import loggerService from '../logger';
import { prepareSignSessionOptions } from '../sign-session';
import {
    CESARIS_CA_SERVER_ADDRESS,
    INTERNAL_SIGNATURES_FORMATS,
} from './constants';
import signer from './signer';

export const getCAServer = (servers, address) => {
    const idx = servers.findIndex((item) => {
        return item.address === address;
    });
    if (idx === -1) {
        return null;
    }
    return servers.splice(idx, 1)[0];
};

export function getConfigMaxFileSize(currentRole) {
    try {
        // New users don't have role
        if (!currentRole) return null;

        let config = currentRole.company.config;

        if (!config) {
            return null;
        }

        config = JSON.parse(config);
        return config.signer_max_file_size
            ? config.signer_max_file_size * 1024 * 1024
            : null;
    } catch (err) {
        loggerService.log(`Error on getConfigMaxFileSize: ${err.message}`);
        return null;
    }
}

export async function getDocument(docId) {
    try {
        const buffer = await io.getAsArrayBuffer(getDocumentUrl(docId));
        return new Uint8Array(buffer);
    } catch (err) {
        throw new Error(`Не вдалося завантажити документ (${err.message}).`);
    }
}

export async function getDocumentVersion(docId, versionId) {
    try {
        const buffer = await io.getAsArrayBuffer(
            getDocumentUrl(docId, true, '', '', '', versionId, true),
        );
        return new Uint8Array(buffer);
    } catch (err) {
        throw new Error(`Не вдалося завантажити документ (${err.message}).`);
    }
}

export async function getLastSignature(docId) {
    const buffer = await io.getAsArrayBuffer(getLastSignatureUrl(docId));
    return new Uint8Array(buffer);
}

export async function getContentForWrappedOrAppendedSignature(docId) {
    // wrapped/appended signature is sign of last signature or document original
    try {
        return await getLastSignature(docId);
    } catch (error) {
        if (error.code === 'object_does_not_exist') {
            return await getDocument(docId);
        }
        throw error;
    }
}

export async function getTTNSignatureContent(docId) {
    const url = `/internal-api/documents/${docId}/ttn/signatures/content`;
    const buffer = await io.getAsArrayBuffer(url);
    return new Uint8Array(buffer);
}

export async function getTTNSignatureHash(docId) {
    const url = `/internal-api/documents/${docId}/ttn/signatures/hash`;
    const data = await io.getAsJson(url);
    return data.hash;
}

export async function getDocumentHash(docId) {
    try {
        const data = await io.getAsJson(
            `/internal-api/documents/${docId}/hash`,
            prepareSignSessionOptions(),
        );
        return data.document_hash;
    } catch (err) {
        loggerService.error('Hash document loading problem', {
            err: err.message,
            docId,
            onLine: navigator.onLine,
        });
        throw new Error(
            `Не вдалося завантажити геш документу (${err.message}).`,
        );
    }
}

export const findCAServerIdx = (caServers, address) => {
    const idx = caServers.findIndex((item) => item.address === address);
    return idx === -1 ? null : idx;
};

export const findCAServerIdxByCAName = (caServerName, caServers) => {
    const idx = caServers.findIndex((item) =>
        item.issuerCNs.includes(caServerName),
    );
    return idx === -1 ? null : idx;
};

export const formatSignResult = (signs) => {
    return signs.reduce((prev, sign) => {
        if (sign.keyType === 'pk')
            return { ...prev, pkSign: sign.eds, pkSignInfo: sign.edsInfo };
        return { ...prev, stampSign: sign.eds, stampSignInfo: sign.edsInfo };
    }, {});
};

/**
 * Convert list of signs to a single object where key and stamp signatures
 * are separated in different attributes of the returned object.
 *
 * @param {import('./types').KeysStateInfo} keys - Object with
 * @param {Array<Object>} signs - List of signatures
 */
export const formatSignResultCtx = (keys, signs) => {
    // Match signatures with keys
    const pkSerialNumber = keys.pk?.keyInfo.serialNumber;
    return signs.reduce((prev, sign) => {
        if (sign.edsInfo.serialNumber === pkSerialNumber) {
            return { ...prev, pkSign: sign.eds, pkSignInfo: sign.edsInfo };
        }
        return { ...prev, stampSign: sign.eds, stampSignInfo: sign.edsInfo };
    }, {});
};

export const getCAServerIdxByFileName = (caServers, pkFileName) => {
    const idx = pkFileName.lastIndexOf('.');
    if (idx === -1) {
        return null;
    }

    const ext = pkFileName.slice(idx).toLowerCase();
    if (ext === '.zs2' || ext === '.sk') {
        return findCAServerIdx(caServers, CA_SERVERS.ukraina);
    } else if (ext === '.jks') {
        return findCAServerIdx(caServers, CA_SERVERS.privat);
    } else if (ext === '.pk8') {
        return findCAServerIdx(caServers, CA_SERVERS.altersign);
    }

    return null;
};

export const getACSK = (caServerIdx, caServers) => {
    const caServer = caServers[caServerIdx];
    if (!caServer) {
        return ACSK.DEFAULT.value;
    }
    switch (caServer.address) {
        case CESARIS_CA_SERVER_ADDRESS:
            return ACSK.CESARIS.value;
        default:
            return ACSK.DEFAULT.value;
    }
};

export const isInternalSignatureFormat = (format) => {
    return INTERNAL_SIGNATURES_FORMATS.includes(format);
};

export async function signDocuments(
    docs,
    getDocumentsArgs,
    keys,
    afterSignCallback,
    beforeMultiSignCallback,
    usbSignerCtx,
    signRequestParameters,
    roleId,
    userEdrpou,
    userEmail,
    widgetSigns,
    kepSigns,
) {
    const signResults = [];

    for (const doc of docs) {
        const { expectedSignatureFormat } = doc;
        const widgetSign = widgetSigns?.find((item) => item.docId === doc.id);
        const kepSign = kepSigns?.find((item) => item.doc.id === doc.id);
        const signResult = await signer.signAndSubmitDocument(
            doc,
            keys,
            roleId,
            userEdrpou,
            userEmail,
            expectedSignatureFormat,
            afterSignCallback,
            usbSignerCtx,
            signRequestParameters,
            widgetSign,
            kepSign,
        );
        signResults.push(signResult);
        // Stop processing if no money
        if (
            !signResult.success &&
            signResult.errorCode === BILLING_OVERLIMIT_ERROR_CODE
        ) {
            break;
        }
    }

    return signResults;
}

export async function massSignDocuments(
    docs,
    getDocumentsArgs,
    keys,
    currentUser,
    afterSignCallback,
    beforeMultiSignCallback,
    usbSignerCtx,
    signRequestParameters,
    data,
    roleId,
    userEdrpou,
    userEmail,
    widgetSigns,
    kepSigns,
) {
    // When continue mass sign `docs.length` can be 0, so return empty sign result
    if (docs.length === 0) {
        return [];
    }

    // Get documents data
    data.ids = docs.map((d) => d.id);
    data.currentUser = currentUser;
    docs = await getDocumentsByPartially(data);

    // Trigger before multi sign callback
    if (beforeMultiSignCallback) beforeMultiSignCallback(docs);

    // Actual documents signing
    return await signDocuments(
        docs,
        getDocumentsArgs,
        keys,
        afterSignCallback,
        beforeMultiSignCallback,
        usbSignerCtx,
        signRequestParameters,
        roleId,
        userEdrpou,
        userEmail,
        widgetSigns,
        kepSigns,
    );
}

export async function superMassSignDocuments(
    docs,
    getDocumentsArgs,
    keys,
    currentUser,
    afterSignCallback,
    beforeMultiSignCallback,
    usbSignerCtx,
    signRequestParameters,
    data,
    roleId,
    userEdrpou,
    userEmail,
    isEsSearchEnabled,
    widgetSigns,
    kepSigns,
) {
    // Get documents data
    data.args = getDocumentsArgs;
    data.isEsSearchEnabled = isEsSearchEnabled;
    data.currentUser = currentUser;
    docs = await getDocumentsByPartially(data);

    // Trigger before multi sign callback
    if (beforeMultiSignCallback) beforeMultiSignCallback(docs);

    // Actual documents signing
    return await signDocuments(
        docs,
        getDocumentsArgs,
        keys,
        afterSignCallback,
        beforeMultiSignCallback,
        usbSignerCtx,
        signRequestParameters,
        roleId,
        userEdrpou,
        userEmail,
        widgetSigns,
        kepSigns,
    );
}

/**
 * Read key context and return idx caServer
 * @param keyFile {File}
 * @param keyPassword {string}
 * @returns {Promise<[VchasnoSigner.KeyCtxCAIdx | null, number | null]>}
 */
export async function autopickServers(keyFile, keyPassword) {
    let key = null;
    let caServerIdx = null;

    try {
        key = await signer.readPKKeyCtxWithCMP(keyFile, keyPassword);
        caServerIdx = key.caServerIdx;
    } catch (error) {
        // do nothing, it's okay
    }

    return [key, caServerIdx];
}
